// ============================================================================
// stage1-import-rfq-text.ts
// ----------------------------------------------------------------------------
// The text a Stage 1 import saves as the RFQ (`formData.rfqContent`), and the
// one question it may ask about it.
//
// The import saves an imported email's clean body, or else the text picked for
// extraction. Job 99201 (2026-10-21): that made a COVERING EMAIL ("Please find
// the attached document with the details of the quote request.") the RFQ while
// the request was the attached PDF, so Stage 3 stopped with
// `incorporation-relationship-unresolved`.
//
// Two release reviews held an automatic swap: every rule for "the email says
// nothing else" let some request text through. Peter, 2026-10-21: "when an
// imported email looks like a covering note, ask the operator once at import
// whether to use the attached request as the RFQ, showing what would be set
// aside. Never swap silently." So:
//   - the import never changes the RFQ text on its own. It asks when the email
//     LOOKS like a covering note (looksLikeCoveringNoteEmail, broad on purpose),
//     or its clean body is empty (the fallback reader's bare forward; a forward
//     read through normal preprocessing keeps its header lines and does not
//     ask), and the attached request is this job's request. It shows the full text of every imported email;
//     the operator chooses. Keep writes the covering email's full text, exactly
//     as the question shows it;
//   - the answer is kept on the project (`rfqCoveringNoteDecision`) and applied
//     again without asking only to the same files, with no email the operator
//     has not seen, while the RFQ is still the text that answer wrote. A
//     question closed without an answer is kept too, as 'unanswered', so Stage 1
//     can offer it again ("Choose the RFQ source");
//   - an RFQ the operator chose (here, or at the Stage 3 Recheck) is never
//     replaced by a later import that asks nothing: the new files stay
//     documents and the operator is told (release review 3, 2026-10-21);
//   - only a manual (Fast-Track) import asks. Peter, 2026-10-21: "In Full AI,
//     don't ask at import." A Full AI import writes what v6.0.110 wrote,
//     except that it keeps an RFQ the operator chose, with a toast offering the
//     new text, and its Stage 3 holds as before. Any Full AI doubt record is
//     separate work (claude/confident-swirles-16da86), not in this change. A run with no operator never asks and never swaps: queued and
//     Quote Run preparation never reach this file (prepare-stage1-evidence.ts
//     writes no RFQ text).
// Contents decide, never file names (Peter, 2026-10-13).
//
// The question is asked only when ALL hold:
//   1. the import is a manual (Fast-Track) import and the kill switch
//      qm_disable_rfq_priority is off;
//   2. the latest email looks like a covering note, or its clean body is
//      empty, and it lists no requested-scope rows;
//   3. exactly one other non-email text (copies of one file count once) reads
//      as a request by its class and by its own text (detectRfqByContent: an AI
//      label alone never decides), takes a verified receipt with verified
//      requested-scope rows, and is the document the import picked, if any;
//   4. the email's Subject request number is that document's own request
//      number (not "Follows previous Job No. 12345"), every request or
//      reference number the document carries is the Subject's, and its strata
//      plan is the Subject's when the Subject names one (never ask about another
//      job's document).
// Anything else keeps the email text, as before.
// ============================================================================
import { isEmailDocument } from './stored-document-text';
import {
    adoptAttachedRequestFormData,
    coveringNoteSaysNothingMore,
    emailTextForReview,
    looksLikeCoveringNoteEmail,
    readsAsRequestByContent,
    requestIdentifiersIn,
    requestNumbersIn,
    subjectLineOf,
    type AttachedRequestMatch,
} from './rfq-attached-request';
import {
    createVerifiedRequestedScope,
    deriveVerifiedRfqContentProvenance,
    extractExplicitRequestedScopeRows,
    fingerprintSourceContent,
    isVerifiedRfqContentProvenance,
    normaliseProvenanceText,
} from '../utils/source-provenance';
import { withoutStaleRfqAuthority } from '../utils/rfq-authority-binding';
import { detectRfqByContent } from '../utils/rfq-content-detector';
import { isFastTrackManualScope, normalizeProjectWorkflowMode } from './project-workflow';
import { stage1EmailCleanBody } from './stage1-email-clean-body';

type LooseRecord = Record<string, unknown>;

export type Stage1CoveringNoteChoice = 'use-attached-request' | 'keep-email';
/** What the project records: an answer, or 'unanswered' (closed without one, or a Use that could not be applied). */
export type Stage1CoveringNoteRecordedChoice = Stage1CoveringNoteChoice | 'unanswered';

/** The operator's answer, kept on the form so the same files never ask twice. */
export interface Stage1CoveringNoteDecisionRecord {
    version: 2;
    choice: Stage1CoveringNoteRecordedChoice;
    emailFingerprint: string;
    requestFingerprint: string;
    /** Every email the question showed: an import that brings another one asks again. */
    shownEmailFingerprints: string[];
    /** The RFQ text the answer wrote: the answer stands only while that is the RFQ. */
    rfqTextFingerprint: string;
    emailDocumentName: string;
    requestDocumentName: string;
    sharedReferences: string[];
    decidedAt: string;
    /** 'stage5': answered at the Stage 5 check (rfq-source-reading.ts). Absent: at a Stage 1 import. */
    where?: 'stage5';
}

export interface Stage1CoveringNoteEmailText {
    documentId: string;
    documentName: string;
    /** The email's full text: what follows its sign-off and any quoted or forwarded message included. */
    text: string;
}

/** One attached request the operator can choose. */
export interface Stage1CoveringNoteRequestOption {
    /** The attached request (`document`) and the covering email (`sourceDocument`). */
    match: AttachedRequestMatch;
    requestDocumentName: string;
    /** Request numbers both texts carry, digits only (e.g. ['99201']). */
    sharedReferences: string[];
    requestFingerprint: string;
}

export interface Stage1CoveringNoteQuestion extends Stage1CoveringNoteRequestOption {
    /**
     * Every attached request the operator can choose, the one described by the
     * fields above first. An import asks about one; Stage 1's "Choose the RFQ
     * source" offers every request the project's documents hold.
     */
    options: Stage1CoveringNoteRequestOption[];
    /** Every imported email, the covering one first. None of it is the RFQ after a yes. */
    setAside: Stage1CoveringNoteEmailText[];
    /**
     * An email says more than "the request is attached" (a line after the
     * sign-off, a forwarded message, another email that asks for work). The
     * question then says so.
     */
    saysMore: boolean;
    /** The covering email has no message of its own: it only forwards one. */
    forwardOnly: boolean;
    /** What Keep writes: the covering email's full text, as the question shows it. */
    keepText: string;
    emailFingerprint: string;
    shownEmailFingerprints: string[];
}

export type Stage1CoveringNoteOutcome =
    | { kind: 'ask'; question: Stage1CoveringNoteQuestion }
    | { kind: 'decided'; question: Stage1CoveringNoteQuestion; choice: Stage1CoveringNoteChoice };

export interface Stage1CoveringNoteInput {
    /** The clean body of the last imported email, or ''. */
    latestEmailCleanBody: string;
    /** The id of the document that body came from. */
    latestEmailDocumentId: string;
    /** The non-email document the import picked as the request, if any. */
    pickedRequestDocument: LooseRecord | null;
    /** This import's documents, after classification. */
    documents: unknown[];
    /** qm_disable_rfq_priority. */
    rfqPriorityDisabled: boolean;
    /** An operator started this import and can answer. */
    attended: boolean;
    /** The project's form as it is now: its stored answer and its RFQ text. */
    formData?: LooseRecord | null;
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');
const idOf = (document: unknown): string => String((document as LooseRecord | null)?.id ?? '');
const rowsOf = (documents: unknown): LooseRecord[] => (Array.isArray(documents)
    ? documents.filter((row): row is LooseRecord => !!row && typeof row === 'object')
    : []);
const contentFingerprint = (row: LooseRecord): string => fingerprintSourceContent(text(row.content));
const isChoice = (value: unknown): value is Stage1CoveringNoteChoice => value === 'use-attached-request' || value === 'keep-email';

/**
 * Whether an import in this workflow may ask. Peter, 2026-10-21: "In Full AI,
 * don't ask at import ... Attended manual imports still ask once." So only a
 * manual (Fast-Track) import asks; a Full AI import writes what v6.0.110 wrote,
 * except that it keeps an RFQ the operator chose. An unknown mode reads as Full
 * AI, the side that asks nothing.
 */
export function stage1ImportAsksAboutCoveringNote(workflowMode: unknown): boolean {
    return isFastTrackManualScope(normalizeProjectWorkflowMode(typeof workflowMode === 'string' ? workflowMode : null));
}

/**
 * The RFQ text an import writes when it asks nothing. A forwarded email with no
 * words of its own has an empty clean body, and the import used to fall through
 * to the picked document, so the forwarded text never reached the RFQ and
 * nobody was asked (release review 3, 2026-10-21). An attended import keeps that
 * email's full text unless the text it would write already holds it. A Full AI
 * import writes what v6.0.110 wrote here.
 */
export function stage1ImportRfqText({
    latestEmailCleanBody,
    latestEmailDocument,
    contentForExtraction,
    attended,
}: {
    latestEmailCleanBody: string;
    latestEmailDocument: unknown;
    contentForExtraction: string;
    attended: boolean;
}): string {
    if (latestEmailCleanBody) return latestEmailCleanBody;
    const fullText = attended && isEmailDocument(latestEmailDocument) ? emailTextForReview(latestEmailDocument) : '';
    if (fullText && !normaliseProvenanceText(contentForExtraction).includes(normaliseProvenanceText(fullText))) return fullText;
    return contentForExtraction;
}

// The project's record about this question, whatever the RFQ text is now.
function recordedDecision(formData: LooseRecord | null | undefined): Stage1CoveringNoteDecisionRecord | null {
    const record = formData?.rfqCoveringNoteDecision as LooseRecord | undefined;
    if (!record || typeof record !== 'object' || record.version !== 2) return null;
    if (!isChoice(record.choice) && record.choice !== 'unanswered') return null;
    return record as unknown as Stage1CoveringNoteDecisionRecord;
}

/** The project's record about this question, while the RFQ is still the text it wrote. */
export function stage1CoveringNoteRecord(formData: LooseRecord | null | undefined): Stage1CoveringNoteDecisionRecord | null {
    const record = recordedDecision(formData);
    // A record stands while the RFQ is the text it wrote. A Stage 3 Recheck, an
    // edit or another import since then retires it (release review 3, #4 and #10).
    if (!record || record.rfqTextFingerprint !== fingerprintSourceContent(text(formData?.rfqContent))) return null;
    return record;
}

/**
 * The operator's answer about exactly this email and this request, while the RFQ
 * is still the text it wrote. The one reader for Stage 3's source step and the
 * Stage 5 doubt as well as the import, so an answer means the same everywhere.
 * 'unanswered' is no answer. `afterAnEdit` also reads an answer whose RFQ text
 * was edited since: Stage 3 passes it when its match still binds the RFQ to this
 * very email, so an edit of the email kept is still that email kept (merge
 * review, 2026-10-22). The import asks again instead.
 */
export function stage1CoveringNoteAnswer(
    formData: LooseRecord | null | undefined,
    { emailFingerprint, requestFingerprint }: { emailFingerprint: string; requestFingerprint: string },
    { afterAnEdit = false }: { afterAnEdit?: boolean } = {},
): Stage1CoveringNoteChoice | null {
    const record = afterAnEdit ? recordedDecision(formData) : stage1CoveringNoteRecord(formData);
    if (!record || !isChoice(record.choice)
        || record.emailFingerprint !== emailFingerprint
        || record.requestFingerprint !== requestFingerprint) return null;
    return record.choice;
}

function storedChoice(formData: LooseRecord | null | undefined, question: Stage1CoveringNoteQuestion): Stage1CoveringNoteChoice | null {
    const choice = stage1CoveringNoteAnswer(formData, question);
    if (!choice) return null;
    // Every email this import shows was shown when the operator answered.
    const stored = stage1CoveringNoteRecord(formData)?.shownEmailFingerprints;
    const shown = new Set(Array.isArray(stored) ? stored : []);
    if (!question.shownEmailFingerprints.every(fingerprint => shown.has(fingerprint))) return null;
    return choice;
}

/** An RFQ text the operator chose that is still the RFQ. */
export interface Stage1ChosenRfq {
    choice: Stage1CoveringNoteRecordedChoice;
    /** The document whose text the RFQ is. */
    documentName: string;
    /** The document the operator chose it over. */
    otherDocumentName: string;
    /** Where the operator chose it. */
    where: 'import' | 'stage3' | 'stage5';
}

/**
 * The RFQ text the operator chose and that is still the RFQ: an attached request
 * they adopted at an import or at the Stage 3 Recheck (its receipt still binds
 * the text to that document) or kept at the Stage 5 check, or an email they kept
 * at an import or at the Stage 5 check. A question left unanswered chose
 * nothing, and neither did QuoteMaker's own best reading at Stage 3
 * (`via: 'stage3-best-reading'`) until the operator kept it at Stage 5.
 */
export function operatorChosenRfq(formData: LooseRecord | null | undefined): Stage1ChosenRfq | null {
    if (!formData || !text(formData.rfqContent).trim()) return null;
    const adoption = formData.rfqSourceAdoption as LooseRecord | undefined;
    const provenance = formData.rfqContentProvenance;
    if (adoption && typeof adoption === 'object'
        && adoption.via !== 'stage3-best-reading'
        && isVerifiedRfqContentProvenance(provenance, formData.rfqContent)
        && String(provenance.sourceId) === String(adoption.toDocumentId ?? '')) {
        return {
            choice: 'use-attached-request',
            documentName: text(adoption.toDocumentName),
            otherDocumentName: text(adoption.fromDocumentName),
            where: adoption.via === 'stage1-import' ? 'import' : 'stage3',
        };
    }
    const record = stage1CoveringNoteRecord(formData);
    if (record && isChoice(record.choice)) {
        const kept = record.choice === 'keep-email';
        return {
            choice: record.choice,
            documentName: kept ? record.emailDocumentName : record.requestDocumentName,
            otherDocumentName: kept ? record.requestDocumentName : record.emailDocumentName,
            where: record.where === 'stage5' ? 'stage5' : 'import',
        };
    }
    return null;
}

/** What an import that kept the RFQ the operator chose says ("The RFQ stays the email X, as you chose at Stage 5"). */
export function stage1KeptRfqSentence(kept: Stage1ChosenRfq): string {
    if (kept.choice === 'unanswered') return `The RFQ stays the email ${kept.documentName} for now, while its question is not answered`;
    const where = kept.where === 'stage3' ? 'at Stage 3' : kept.where === 'stage5' ? 'at Stage 5' : 'in Stage 1';
    return `The RFQ stays ${kept.choice === 'keep-email' ? 'the email' : 'the attached request'} ${kept.documentName}, as you chose ${where}`;
}

interface EmailReading {
    forwardOnly: boolean;
    emailText: string;
}

// The email side of the question: it looks like a covering note, or forwards a
// message without a word of its own (release review 3, #2), and lists no rows.
function readCoveringEmail(email: LooseRecord, cleanBody: string): EmailReading | null {
    const forwardOnly = !cleanBody;
    const emailText = cleanBody || emailTextForReview(email);
    if (!emailText) return null;
    if (!forwardOnly && !looksLikeCoveringNoteEmail(emailText)) return null;
    if (extractExplicitRequestedScopeRows(emailText).length > 0) return null;
    return { forwardOnly, emailText };
}

// One attached request, if the question may offer it: its own text reads as a
// request, it takes a verified receipt with verified requested-scope rows, and
// it is this job's request.
function requestOption(
    document: LooseRecord,
    requests: LooseRecord[],
    rows: LooseRecord[],
    email: LooseRecord,
    emailText: string,
): Stage1CoveringNoteRequestOption | null {
    const requestText = text(document.content);
    // Its own text must read as a request, whatever label the AI added.
    if (!detectRfqByContent(requestText).isRfq) return null;
    // The receipt also proves the document was read to the end (source-provenance).
    // Copies of one file bind to the first copy, which the adoption then uses.
    const receipt = deriveVerifiedRfqContentProvenance({ rfqContent: requestText, documents: rows });
    const bound = receipt ? requests.find(row => idOf(row) === String(receipt.sourceId)) : undefined;
    if (!receipt || !bound || contentFingerprint(bound) !== contentFingerprint(document)) return null;
    if (!createVerifiedRequestedScope(requestText, receipt)) return null;

    // Never another job's request (release review 3, #12).
    const subject = subjectLineOf(email.content, emailText);
    const emailIds = requestIdentifiersIn(subject);
    const documentIds = requestIdentifiersIn(requestText);
    const sharedReferences = [...requestNumbersIn(subject)].filter(number => documentIds.own.has(number)).sort();
    if (sharedReferences.length === 0) return null;
    if ([...documentIds.all].some(number => !emailIds.all.has(number))) return null;
    if (emailIds.plans.size > 0 && [...documentIds.plans].some(plan => !emailIds.plans.has(plan))) return null;

    return {
        match: { document: bound, sourceDocument: email, sharedReferences },
        requestDocumentName: text(bound.name),
        sharedReferences,
        requestFingerprint: contentFingerprint(bound),
    };
}

function buildQuestion(
    email: LooseRecord,
    reading: EmailReading,
    rows: LooseRecord[],
    options: Stage1CoveringNoteRequestOption[],
): Stage1CoveringNoteQuestion {
    const emailFingerprint = contentFingerprint(email);
    const otherEmails = rows.filter(row => row !== email && isEmailDocument(row));
    const emails = [email, ...otherEmails.filter((row, index) => contentFingerprint(row) !== emailFingerprint
        && otherEmails.findIndex(other => contentFingerprint(other) === contentFingerprint(row)) === index)];
    const setAside = emails.map(row => ({
        documentId: idOf(row),
        documentName: text(row.name) || 'Email',
        text: emailTextForReview(row),
    }));
    return {
        ...options[0]!,
        options,
        setAside,
        saysMore: reading.forwardOnly || setAside.some(entry => !coveringNoteSaysNothingMore(entry.text)),
        forwardOnly: reading.forwardOnly,
        // Keep writes what the question shows (release review 4, #4 and #12).
        keepText: setAside[0]!.text,
        emailFingerprint,
        shownEmailFingerprints: emails.map(contentFingerprint),
    };
}

/** The question this import must ask, the stored answer to it, or null for no question. */
export function findStage1CoveringNoteQuestion(input: Stage1CoveringNoteInput): Stage1CoveringNoteOutcome | null {
    const { latestEmailCleanBody, latestEmailDocumentId, pickedRequestDocument, documents, rfqPriorityDisabled, attended } = input;
    if (!attended || rfqPriorityDisabled || !Array.isArray(documents)) return null;

    const rows = rowsOf(documents);
    const email = rows.find(row => idOf(row) === latestEmailDocumentId && latestEmailDocumentId !== '');
    if (!email || !isEmailDocument(email)) return null;
    const reading = readCoveringEmail(email, latestEmailCleanBody);
    if (!reading) return null;

    // Every other text that reads as a request counts, read in full or not: a
    // second request that could not be read is a reason not to ask. A file
    // imported twice is one request.
    const requests = rows.filter(row => row !== email && !isEmailDocument(row) && readsAsRequestByContent(row));
    const requestFingerprints = new Set(requests.map(contentFingerprint));
    if (requestFingerprints.size !== 1) return null;
    if (pickedRequestDocument && contentFingerprint(pickedRequestDocument) !== [...requestFingerprints][0]) return null;
    const option = requestOption(requests[0]!, requests, rows, email, reading.emailText);
    if (!option) return null;

    const question = buildQuestion(email, reading, rows, [option]);
    const choice = storedChoice(input.formData, question);
    return choice ? { kind: 'decided', question, choice } : { kind: 'ask', question };
}

/**
 * The question again, from Stage 1 ("Change answer", or "Choose the RFQ source"
 * after a question closed without an answer). Release reviews 3 and 4: after
 * Keep, or after the question was closed, Fast-Track never reaches a Stage 3
 * hold or its Recheck, so this is the way back. It offers every attached request
 * the project's documents now hold that the question could offer, the one the
 * record names first, so a request imported later is a choice, not a refusal.
 * Null when the RFQ is no longer the text the record wrote, or no request can
 * be offered.
 */
export function reopenStage1CoveringNoteQuestion(
    formData: LooseRecord | null | undefined,
    documents: unknown[],
    { rfqPriorityDisabled = false }: { rfqPriorityDisabled?: boolean } = {},
): Stage1CoveringNoteQuestion | null {
    if (rfqPriorityDisabled) return null;
    const record = stage1CoveringNoteRecord(formData);
    if (!record) return null;
    const rows = rowsOf(documents);
    const email = rows.find(row => isEmailDocument(row) && contentFingerprint(row) === record.emailFingerprint);
    if (!email) return null;
    const cleanBody = typeof email.cleanBody === 'string' ? email.cleanBody : stage1EmailCleanBody(emailTextForReview(email));
    const reading = readCoveringEmail(email, cleanBody);
    if (!reading) return null;

    const requests = rows.filter(row => row !== email && !isEmailDocument(row) && readsAsRequestByContent(row));
    const distinct = requests.filter((row, index) => requests.findIndex(other => contentFingerprint(other) === contentFingerprint(row)) === index);
    const options = distinct
        .map(row => requestOption(row, requests, rows, email, reading.emailText))
        .filter((option): option is Stage1CoveringNoteRequestOption => !!option)
        .sort((a, b) => Number(b.requestFingerprint === record.requestFingerprint) - Number(a.requestFingerprint === record.requestFingerprint));
    if (options.length === 0) return null;
    return buildQuestion(email, reading, rows, options);
}

/** The question with the operator's pick of its options as the request it is about. */
export function selectStage1CoveringNoteOption(question: Stage1CoveringNoteQuestion, index: number): Stage1CoveringNoteQuestion {
    const option = question.options[index];
    return option ? { ...question, ...option } : question;
}

// Importing the same files again adds second copies, and the receipt binds to
// the first copy of identical text (smoke 2026-10-21: the stored "use" then fell
// back to the email). The chosen request is itself an exact match, so the
// receipt only ever picks an exact copy of it; the adoption goes through that copy.
function onIdenticalCopy(match: AttachedRequestMatch, documents: unknown[]): AttachedRequestMatch {
    const receipt = deriveVerifiedRfqContentProvenance({ rfqContent: text(match.document.content), documents });
    if (!receipt || String(receipt.sourceId) === idOf(match.document)) return match;
    const copy = rowsOf(documents).find(row => idOf(row) === String(receipt.sourceId));
    return copy ? { ...match, document: copy } : match;
}

// A new RFQ text: the record of an earlier swap and the receipts of the old
// text no longer describe it (release review 3, #4).
function withRfqText(formData: LooseRecord, rfqText: string): LooseRecord {
    if (fingerprintSourceContent(text(formData.rfqContent)) === fingerprintSourceContent(rfqText)) return { ...formData };
    const { rfqSourceAdoption: _replacedAdoption, ...rest } = formData;
    return { ...withoutStaleRfqAuthority(rest), rfqContent: rfqText };
}

export interface Stage1ImportRfqTextResult {
    formData: LooseRecord;
    /** The attached request is now the RFQ. */
    adopted: boolean;
    /** Nothing was written: the RFQ is still the one the operator chose (or, with `protectUnanswered`, the email a question left unanswered). */
    keptChosen: Stage1ChosenRfq | null;
}

const sameText = (a: unknown, b: unknown): boolean => fingerprintSourceContent(text(a)) === fingerprintSourceContent(text(b));

/**
 * The record of an answer, for the form whose RFQ text the answer wrote. The one
 * writer of the record's shape: the import's question and the Stage 5 "Use the
 * email instead" (rfq-source-reading.ts) both store through it.
 */
export function stage1CoveringNoteDecision({
    choice,
    emailFingerprint,
    requestFingerprint,
    shownEmailFingerprints,
    form,
    emailDocumentName,
    requestDocumentName,
    sharedReferences,
    decidedAt,
    where,
}: {
    choice: Stage1CoveringNoteRecordedChoice;
    emailFingerprint: string;
    requestFingerprint: string;
    shownEmailFingerprints: string[];
    /** The form as written: the record stands while its RFQ text is the RFQ. */
    form: LooseRecord;
    emailDocumentName: string;
    requestDocumentName: string;
    sharedReferences: string[];
    decidedAt: string;
    where?: Stage1CoveringNoteDecisionRecord['where'];
}): Stage1CoveringNoteDecisionRecord {
    return {
        version: 2,
        choice,
        emailFingerprint,
        requestFingerprint,
        shownEmailFingerprints: [...shownEmailFingerprints],
        rfqTextFingerprint: fingerprintSourceContent(text(form.rfqContent)),
        emailDocumentName,
        requestDocumentName,
        sharedReferences: [...sharedReferences],
        decidedAt,
        ...(where ? { where } : {}),
    };
}

function decisionRecord(
    question: Stage1CoveringNoteQuestion,
    choice: Stage1CoveringNoteRecordedChoice,
    form: LooseRecord,
    decidedAt: string,
): Stage1CoveringNoteDecisionRecord {
    return stage1CoveringNoteDecision({
        choice,
        emailFingerprint: question.emailFingerprint,
        requestFingerprint: question.requestFingerprint,
        shownEmailFingerprints: question.shownEmailFingerprints,
        form,
        emailDocumentName: question.setAside[0]?.documentName || '',
        requestDocumentName: question.requestDocumentName,
        sharedReferences: question.sharedReferences,
        decidedAt,
    });
}

// The email a question left unanswered, while it is still the RFQ.
function unansweredRfq(formData: LooseRecord | null | undefined): Stage1ChosenRfq | null {
    const record = stage1CoveringNoteRecord(formData);
    if (!record || record.choice !== 'unanswered') return null;
    return { choice: 'unanswered', documentName: record.emailDocumentName, otherDocumentName: record.requestDocumentName, where: 'import' };
}

/** The RFQ as the operator would name it ("the email X", "the attached request Y"), or "as it is". */
export function stage1RfqDescription(formData: LooseRecord | null | undefined): string {
    const chosen = operatorChosenRfq(formData) || unansweredRfq(formData);
    if (!chosen) return 'as it is';
    return chosen.choice === 'use-attached-request' ? `the attached request ${chosen.documentName}` : `the email ${chosen.documentName}`;
}

/**
 * The record the operator answered from is still the project's record, for the
 * same RFQ text (release review 5, S14): an import that wrote while the question
 * was open, or any other change, means the answer no longer fits and is refused.
 */
export function stage1CoveringNoteRecordUnchanged(
    before: Stage1CoveringNoteDecisionRecord | null,
    formData: LooseRecord | null | undefined,
): boolean {
    const now = stage1CoveringNoteRecord(formData);
    return !!before && !!now
        && now.choice === before.choice
        && now.decidedAt === before.decidedAt
        && now.emailFingerprint === before.emailFingerprint
        && now.rfqTextFingerprint === before.rfqTextFingerprint;
}

/**
 * The form after an import writes its RFQ text.
 *   - `choice` 'use-attached-request': the attached request becomes the RFQ
 *     through the one adoption path (receipts minted, `rfqSourceAdoption`
 *     recorded). If its receipt no longer binds, NOTHING changes: the RFQ and
 *     the record stay as they were (release review 5, S9).
 *   - 'keep-email', or a question closed without a choice: the question's
 *     `keepText`.
 *   - no question: `rfqText`.
 * A write that answers no question never replaces an RFQ the operator chose
 * (`keptChosen`), unless `replaceChosen` says the operator asked for it.
 * `protectUnanswered` (the same drop that left a question unanswered) protects
 * the email that question kept for now in the same way (release review 5, S13).
 * `record` keeps the outcome on the form: the answer, or 'unanswered' for a
 * question not answered yet (written before the question opens, so a reload
 * keeps it), so Stage 1 can offer the question again.
 */
export function applyStage1ImportRfqText(formData: LooseRecord, {
    rfqText,
    question = null,
    choice = null,
    record = false,
    documents = [],
    decidedAt,
    replaceChosen = false,
    protectUnanswered = false,
}: {
    rfqText: string;
    question?: Stage1CoveringNoteQuestion | null;
    choice?: Stage1CoveringNoteChoice | null;
    record?: boolean;
    documents?: unknown[];
    decidedAt: string;
    replaceChosen?: boolean;
    protectUnanswered?: boolean;
}): Stage1ImportRfqTextResult {
    if (choice === 'use-attached-request' && question) {
        const { rfqSourceAdoption: _replacedAdoption, ...base } = formData;
        const next = adoptAttachedRequestFormData(base, documents, [], onIdenticalCopy(question.match, documents), decidedAt, 'stage1-import');
        if (next) {
            if (record) next.rfqCoveringNoteDecision = decisionRecord(question, 'use-attached-request', next, decidedAt);
            return { formData: next, adopted: true, keptChosen: null };
        }
        // A question nothing has recorded yet, whose email text already is the
        // RFQ, is recorded as unanswered; nothing else changes.
        const form: LooseRecord = { ...formData };
        if (record && !stage1CoveringNoteRecord(formData) && sameText(formData.rfqContent, question.keepText)) {
            form.rfqCoveringNoteDecision = decisionRecord(question, 'unanswered', form, decidedAt);
        }
        return { formData: form, adopted: false, keptChosen: null };
    }
    const kept = !!question && choice === 'keep-email';
    // A stored Keep applied again leaves the RFQ as the text that answer wrote.
    // The Stage 5 "Use the email instead" writes the email's clean body, and
    // writing the full text here would retire the answer (merge review, 2026-10-22).
    const reapplied = kept && !record && stage1CoveringNoteAnswer(formData, question) === 'keep-email';
    const writeText = reapplied ? text(formData.rfqContent) : question ? question.keepText : rfqText;
    if (!kept && !replaceChosen) {
        const chosen = operatorChosenRfq(formData) || (protectUnanswered ? unansweredRfq(formData) : null);
        if (chosen && !sameText(writeText, formData.rfqContent)) {
            return { formData, adopted: false, keptChosen: chosen };
        }
    }
    const form: LooseRecord = withRfqText(formData, writeText);
    if (record && question) form.rfqCoveringNoteDecision = decisionRecord(question, kept ? 'keep-email' : 'unanswered', form, decidedAt);
    return { formData: form, adopted: false, keptChosen: null };
}
